import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { CheckCircle, Camera, Film } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_VIDEO_SECONDS = 90;

export default function SkickaBild() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const preTema = searchParams.get("tema") || "";
  const [form, setForm] = useState({ titel: "", beskrivning: "", fotograf: "", ort: "", samtycke: false, tema: preTema });
  const [teman, setTeman] = useState([]);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaTyp, setMediaTyp] = useState(null); // "foto" | "video"
  const [videoSec, setVideoSec] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/bilder/teman`).then(r => setTeman(r.data.filter(t => t.aktiv))).catch(() => {});
  }, []);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleFilePick = (file) => {
    if (!file) return;
    setErrors((prev) => ({ ...prev, bild: null }));
    const isVideo = file.type.startsWith("video/");
    if (isVideo) {
      if (file.size > 60 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, bild: "Videofilen är för stor (max 60 MB)" }));
        return;
      }
      // Measure duration client-side
      const video = document.createElement("video");
      video.preload = "metadata";
      const url = URL.createObjectURL(file);
      video.src = url;
      video.onloadedmetadata = () => {
        const duration = video.duration;
        if (duration > MAX_VIDEO_SECONDS) {
          setErrors((prev) => ({ ...prev, bild: `Videon får vara högst ${MAX_VIDEO_SECONDS} sekunder (din är ${Math.round(duration)} s)` }));
          return;
        }
        setMediaFile(file);
        setMediaPreview(url);
        setMediaTyp("video");
        setVideoSec(duration);
      };
      video.onerror = () => {
        setErrors((prev) => ({ ...prev, bild: "Kunde inte läsa videofilen." }));
      };
    } else {
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, bild: "Bilden är för stor (max 5 MB)" }));
        return;
      }
      setMediaFile(file);
      setMediaPreview(URL.createObjectURL(file));
      setMediaTyp("foto");
      setVideoSec(null);
    }
  };

  const resetMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    setMediaTyp(null);
    setVideoSec(null);
  };

  const validate = () => {
    const e = {};
    if (!form.titel.trim()) e.titel = t("skicka_bild.titel_label");
    if (!form.fotograf.trim()) e.fotograf = t("skicka_bild.fotograf_label");
    if (!mediaFile) e.bild = t("skicka_bild.bild_label");
    if (!form.tema) e.tema = t("skicka_bild.kategori_label");
    if (!form.samtycke) e.samtycke = t("skicka_bild.samtycke");
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", mediaFile);
      const uploadRes = await axios.post(`${API}/upload`, fd);
      const bild_url = uploadRes.data.url;

      await axios.post(`${API}/bilder/submit`, {
        titel: form.titel,
        beskrivning: form.beskrivning || null,
        fotograf: form.fotograf,
        ort: form.ort || null,
        bild_url,
        samtycke: form.samtycke,
        tema: form.tema || null,
        media_typ: mediaTyp,
        varaktighet_sek: videoSec,
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErrors({ submit: typeof detail === "string" ? detail : "Något gick fel. Försök igen." });
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div data-testid="bild-confirmation" className="max-w-2xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="text-center sm:text-left">
          <CheckCircle size={48} className="text-pine mb-4 mx-auto sm:mx-0" />
          <h1 className="font-heading text-3xl font-medium text-ink mb-3">Tack för ditt bidrag!</h1>
          <p className="text-lg text-muted-foreground mb-2">Vi har tagit emot {mediaTyp === "video" ? "din video" : "din bild"}. Redaktionen granskar och publicerar det så snart som möjligt.</p>
          <p className="text-base text-muted-foreground mb-8">Du behöver inte göra något mer.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/bilder" data-testid="link-tillbaka-bilder" className="inline-flex items-center justify-center bg-falu hover:bg-falu-hover text-white font-body font-medium text-base px-5 py-3 rounded-md transition-colors no-underline">Visa bildsamlingen</Link>
            <Link to="/" data-testid="link-tillbaka-start" className="inline-flex items-center justify-center border-2 border-pine text-pine hover:bg-bone-alt font-body font-medium text-base px-5 py-3 rounded-md transition-colors no-underline">Tillbaka till startsidan</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="skicka-bild-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <h1 className="font-heading text-3xl sm:text-4xl font-medium text-ink mb-3">Skicka in bild eller kortfilm</h1>
      <p className="text-lg text-muted-foreground mb-8">Dela en bild eller kort video (max 90 sekunder) från bygden. Alla bidrag granskas av redaktionen innan de publiceras i bildsamlingen.</p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <FieldGroup label="Bild eller video" error={errors.bild} required>
          <div className="border-2 border-dashed border-warm-border rounded-lg p-6 text-center hover:border-pine/50 transition-colors">
            {mediaPreview ? (
              <div>
                {mediaTyp === "video" ? (
                  <video src={mediaPreview} controls className="max-h-64 mx-auto rounded-lg mb-3" />
                ) : (
                  <img src={mediaPreview} alt="Förhandsgranskning" className="max-h-64 mx-auto rounded-lg mb-3" />
                )}
                {videoSec !== null && <p className="text-xs text-muted-foreground mb-2">Längd: {Math.round(videoSec)} sekunder</p>}
                <button type="button" onClick={resetMedia} className="text-sm text-falu hover:text-falu-hover">Byt fil</button>
              </div>
            ) : (
              <label className="cursor-pointer flex flex-col items-center gap-2">
                <div className="flex items-center gap-3 text-muted-foreground/50">
                  <Camera size={32} />
                  <Film size={32} />
                </div>
                <span className="text-base text-muted-foreground">Klicka för att välja bild eller video</span>
                <span className="text-sm text-muted-foreground/70">Bild: JPG, PNG, WebP (max 5 MB). Video: MP4, MOV, WebM &mdash; max 90 s och 60 MB.</span>
                <input
                  data-testid="field-bild"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
                  className="hidden"
                  onChange={(e) => handleFilePick(e.target.files[0])}
                />
              </label>
            )}
          </div>
        </FieldGroup>

        <FieldGroup label="Titel" help='T.ex. "Midnattssol över Fjällsjöälven" eller "Vinter i Backe"' error={errors.titel} required>
          <input data-testid="field-titel" type="text" value={form.titel} onChange={e => set("titel", e.target.value)} className="w-full min-h-[48px] px-4 py-3 border border-warm-border rounded-lg text-base bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
        </FieldGroup>

        <FieldGroup label="Beskrivning" help="Berätta kort om bilden eller videon &mdash; var den är tagen, när, eller vad den visar.">
          <textarea data-testid="field-beskrivning" value={form.beskrivning} onChange={e => set("beskrivning", e.target.value)} rows={3} className="w-full px-4 py-3 border border-warm-border rounded-lg text-base bg-white focus:ring-2 focus:ring-pine focus:outline-none resize-y" />
        </FieldGroup>

        <FieldGroup label="Ditt namn" help="Visas som fotograf/upphovsperson." error={errors.fotograf} required>
          <input data-testid="field-fotograf" type="text" value={form.fotograf} onChange={e => set("fotograf", e.target.value)} className="w-full min-h-[48px] px-4 py-3 border border-warm-border rounded-lg text-base bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
        </FieldGroup>

        <FieldGroup label="Plats" help="T.ex. Backe, Rossön, Fjällsjöälven">
          <input data-testid="field-ort" type="text" value={form.ort} onChange={e => set("ort", e.target.value)} className="w-full min-h-[48px] px-4 py-3 border border-warm-border rounded-lg text-base bg-white focus:ring-2 focus:ring-pine focus:outline-none" />
        </FieldGroup>

        {teman.length > 0 && (
          <FieldGroup label={t("skicka_bild.kategori_label") + " *"} help={t("skicka_bild.kategori_help")}>
            <div data-testid="tema-chips" className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {teman.map(tm => (
                <button
                  type="button"
                  key={tm.slug}
                  data-testid={`tema-chip-${tm.slug}`}
                  onClick={() => set("tema", tm.slug)}
                  className={`text-left px-4 py-3 rounded-sm border transition-all ${
                    form.tema === tm.slug
                      ? "bg-pine text-parchment border-pine shadow-sm"
                      : "bg-parchment text-ink border-warm-border hover:border-pine/60"
                  }`}
                >
                  <span className="block font-heading text-base font-semibold leading-tight">{tm.namn}</span>
                  <span className={`block text-xs mt-0.5 leading-snug ${form.tema === tm.slug ? "text-parchment/80" : "text-bark/70"}`}>{tm.beskrivning}</span>
                </button>
              ))}
            </div>
            {errors.tema && <p className="text-falu text-sm mt-2">{t("skicka_bild.kategori_label")}</p>}
          </FieldGroup>
        )}

        <div className="flex items-start gap-3 mt-2">
          <input data-testid="field-samtycke" type="checkbox" checked={form.samtycke} onChange={e => set("samtycke", e.target.checked)} className="mt-1.5 w-5 h-5 accent-pine rounded" />
          <label className="text-base text-ink leading-relaxed">
            {/* Etappe 4.8 — uppdaterad bekräftelse-text med länkar */}
            Jag bekräftar att jag har rätt att skicka in bilden/videon och att vidalv.live får granska, publicera och behålla materialet enligt{" "}
            <Link to="/villkor-inskickat" target="_blank" className="text-falu underline">villkoren för inskickat material</Link> och{" "}
            <Link to="/integritet" target="_blank" className="text-falu underline">integritetspolicyn</Link>.
          </label>
        </div>
        {errors.samtycke && <p className="text-falu text-sm -mt-4">{errors.samtycke}</p>}

        {/* Etappe 4.8 — dataskyddshinweis */}
        <p data-testid="dataskydd-hint" className="text-xs text-bark/60 leading-relaxed -mt-2">
          Vi använder dina uppgifter för att kunna granska och kontakta dig vid frågor. Läs mer i vår{" "}
          <Link to="/integritet" className="text-falu hover:text-falu-hover">integritetspolicy</Link>.
        </p>

        {errors.submit && <p className="text-falu text-base">{errors.submit}</p>}

        <button
          type="submit"
          disabled={submitting}
          data-testid="submit-bild-button"
          className="w-full sm:w-auto bg-falu hover:bg-falu-hover disabled:opacity-50 text-white font-body font-medium text-lg px-6 py-4 rounded-md transition-colors mt-2"
        >
          {submitting ? "Skickar..." : (mediaTyp === "video" ? "Skicka in video" : "Skicka in bild")}
        </button>
      </form>
    </div>
  );
}

function FieldGroup({ label, help, error, required, children }) {
  return (
    <div>
      <label className="block text-base font-medium text-ink mb-1">
        {label}{required && <span className="text-falu ml-1">*</span>}
      </label>
      {help && <p className="text-sm text-muted-foreground mb-2">{help}</p>}
      {children}
      {error && <p className="text-falu text-sm mt-1">{error}</p>}
    </div>
  );
}
